import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { X, Clock, Users, Check } from 'lucide-react';
import { client } from '../api/client';
import { getRefCode } from '../lib/referral';
import { normalizeHKPhone } from '../lib/phone';
import { localizedName, localizedDescription } from '../lib/localized';

interface RoomType {
  id: number;
  name: string;
  nameEn: string | null;
  nameZh: string | null;
  nameZhCn: string | null;
  description: string | null;
  descriptionEn: string | null;
  descriptionZh: string | null;
  descriptionZhCn: string | null;
  pricePerNight: number;
  maxGuests: number | null;
  inventory: number;
  imageUrl: string | null;
  amenities: string | null;
  bedType: string | null;
  view: string | null;
  sizeSqm: number | null;
  occupancy: string | null;
  gallery: string | null;
  features: string | null;
}

interface Facility {
  icon: string;
  label: string;
}

interface Activity {
  image: string;
  name: string;
  description: string;
}

interface LocationDetails {
  description: string;
  mapImage: string;
  nearby: string[];
}

interface Story {
  title: string;
  content: string;
}

interface ExperienceItem {
  id: number;
  name: string;
  nameEn: string | null;
  nameZh: string | null;
  nameZhCn: string | null;
  slug: string;
  description: string | null;
  descriptionEn: string | null;
  descriptionZh: string | null;
  descriptionZhCn: string | null;
  duration: string | null;
  groupSize: string | null;
  includes: string | null;
  price: number | null;
  priceNote: string | null;
  imageUrl: string | null;
  type: 'experience' | 'retreat';
}

interface Property {
  id: number;
  name: string;
  nameEn: string | null;
  nameZh: string | null;
  nameZhCn: string | null;
  description: string | null;
  descriptionEn: string | null;
  descriptionZh: string | null;
  descriptionZhCn: string | null;
  location: string | null;
  pricePerNight: number;
  maxGuests: number | null;
  imageUrl: string | null;
  amenities: string | null;
  gallery: string | null;
  facilities: string | null;
  locationDetails: string | null;
  story: string | null;
  roomTypes: RoomType[];
  experiences: ExperienceItem[];
  retreats: ExperienceItem[];
}

function safeJsonParse<T>(value: string | null | undefined, fallback: T): T {
  if (!value) return fallback;
  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
}

function scrollToInquiry() {
  const el = document.getElementById('inquiry-form');
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const input = el.querySelector('input[type="text"]') as HTMLInputElement | null;
    if (input) input.focus({ preventScroll: true });
  }
}

export default function PropertyDetailPage() {
  const { id } = useParams();
  const { t } = useTranslation('properties');
  const [property, setProperty] = useState<Property | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedRoom, setSelectedRoom] = useState<RoomType | null>(null);
  const [selectedActivities, setSelectedActivities] = useState<ExperienceItem[]>([]);
  const [selectedExperience, setSelectedExperience] = useState<ExperienceItem | null>(null);
  const [activeImage, setActiveImage] = useState(0);
  const [roomGalleryIndex, setRoomGalleryIndex] = useState(0);

  // Experience inquiry form state
  const [inquiryName, setInquiryName] = useState('');
  const [inquiryEmail, setInquiryEmail] = useState('');
  const [inquiryPhone, setInquiryPhone] = useState('+852 ');
  const [inquiryCheckIn, setInquiryCheckIn] = useState('');
  const [inquiryDays, setInquiryDays] = useState(3);
  const [inquiryMessage, setInquiryMessage] = useState('');
  const [inquirySubmitted, setInquirySubmitted] = useState(false);

  useEffect(() => {
    fetchProperty();
  }, [id]);

  useEffect(() => {
    setActiveImage(0);
  }, [property?.id]);

  useEffect(() => {
    setRoomGalleryIndex(0);
  }, [selectedRoom?.id]);

  const demoProperties: Record<number, Property> = {
    1: {
      id: 1,
      name: 'Stay Mikado',
      nameZh: t('demo.1.nameZh'),
      description: 'A boutique overwater villa collection in the Maldives.',
      descriptionZh: t('demo.1.description'),
      location: 'North Malé Atoll, Maldives',
      pricePerNight: 4800,
      maxGuests: 4,
      imageUrl:
        'https://images.unsplash.com/photo-1573843981267-be1999ff37cd?w=1200&q=80',
      amenities: JSON.stringify(
        t('demo.1.amenities', { returnObjects: true }) as string[]
      ),
      gallery: JSON.stringify([
        'https://images.unsplash.com/photo-1573843981267-be1999ff37cd?w=1200&q=80',
        'https://images.unsplash.com/photo-1582719508461-905c673771fd?w=800&q=80',
        'https://images.unsplash.com/photo-1590523741831-ab7e8b8f9c7f?w=800&q=80',
        'https://images.unsplash.com/photo-1544144433-d50aff500b91?w=800&q=80',
        'https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?w=800&q=80',
        'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=800&q=80',
      ]),
      facilities: JSON.stringify(
        t('demo.1.facilities', { returnObjects: true }) as Facility[]
      ),
      locationDetails: JSON.stringify(
        t('demo.1.location', { returnObjects: true }) as LocationDetails
      ),
      story: JSON.stringify(
        t('demo.1.story', { returnObjects: true }) as Story
      ),
      roomTypes: [
        {
          id: 101,
          name: 'Lagoon Villa',
          nameZh: t('demo.1.rooms.lagoon.nameZh'),
          description: 'Overwater villa with lagoon views.',
          descriptionZh: t('demo.1.rooms.lagoon.description'),
          pricePerNight: 4800,
          maxGuests: 2,
          inventory: 3,
          imageUrl:
            'https://images.unsplash.com/photo-1582719508461-905c673771fd?w=600',
          amenities: JSON.stringify(
            t('demo.1.rooms.lagoon.amenities', { returnObjects: true }) as string[]
          ),
          bedType: 'King',
          view: t('demo.1.rooms.lagoon.view'),
          sizeSqm: 120,
          occupancy: t('demo.1.rooms.lagoon.occupancy'),
          gallery: JSON.stringify([
            'https://images.unsplash.com/photo-1582719508461-905c673771fd?w=800&q=80',
            'https://images.unsplash.com/photo-1590523741831-ab7e8b8f9c7f?w=800&q=80',
          ]),
          features: JSON.stringify(
            t('demo.1.rooms.lagoon.features', { returnObjects: true }) as string[]
          ),
        },
        {
          id: 102,
          name: 'Ocean Suite',
          nameZh: t('demo.1.rooms.ocean.nameZh'),
          description: 'Spacious suite with private pool.',
          descriptionZh: t('demo.1.rooms.ocean.description'),
          pricePerNight: 7800,
          maxGuests: 4,
          inventory: 2,
          imageUrl:
            'https://images.unsplash.com/photo-1590523741831-ab7e8b8f9c7f?w=600',
          amenities: JSON.stringify(
            t('demo.1.rooms.ocean.amenities', { returnObjects: true }) as string[]
          ),
          bedType: '2 King',
          view: t('demo.1.rooms.ocean.view'),
          sizeSqm: 220,
          occupancy: t('demo.1.rooms.ocean.occupancy'),
          gallery: JSON.stringify([
            'https://images.unsplash.com/photo-1590523741831-ab7e8b8f9c7f?w=800&q=80',
            'https://images.unsplash.com/photo-1544144433-d50aff500b91?w=800&q=80',
          ]),
          features: JSON.stringify(
            t('demo.1.rooms.ocean.features', { returnObjects: true }) as string[]
          ),
        },
      ],
      experiences: [],
      retreats: [],
    },
    2: {
      id: 2,
      name: 'Velaa Private',
      nameZh: t('demo.2.nameZh'),
      description: 'An exclusive private island retreat for the ultimate privacy.',
      descriptionZh: t('demo.2.description'),
      location: 'Noonu Atoll, Maldives',
      pricePerNight: 12800,
      maxGuests: 12,
      imageUrl:
        'https://images.unsplash.com/photo-1688949078626-a358f500e063?w=1200&q=80',
      amenities: JSON.stringify(
        t('demo.2.amenities', { returnObjects: true }) as string[]
      ),
      gallery: JSON.stringify([
        'https://images.unsplash.com/photo-1688949078626-a358f500e063?w=1200&q=80',
        'https://images.unsplash.com/photo-1544550581-5f7ceaf7f992?w=800&q=80',
        'https://images.unsplash.com/photo-1559339352-11d035aa65de?w=800&q=80',
        'https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=800&q=80',
        'https://images.unsplash.com/photo-1437719417032-8595fd9e9dc6?w=800&q=80',
        'https://images.unsplash.com/photo-1602002418082-a4443e081dd1?w=800&q=80',
      ]),
      facilities: JSON.stringify(
        t('demo.2.facilities', { returnObjects: true }) as Facility[]
      ),
      locationDetails: JSON.stringify(
        t('demo.2.location', { returnObjects: true }) as LocationDetails
      ),
      story: JSON.stringify(
        t('demo.2.story', { returnObjects: true }) as Story
      ),
      roomTypes: [
        {
          id: 201,
          name: 'Lagoon Villa',
          nameZh: t('demo.2.rooms.lagoon.nameZh'),
          description: 'Overwater villa with lagoon views.',
          descriptionZh: t('demo.2.rooms.lagoon.description'),
          pricePerNight: 12800,
          maxGuests: 2,
          inventory: 3,
          imageUrl:
            'https://images.unsplash.com/photo-1582719508461-905c673771fd?w=600',
          amenities: JSON.stringify(
            t('demo.2.rooms.lagoon.amenities', { returnObjects: true }) as string[]
          ),
          bedType: 'King',
          view: t('demo.2.rooms.lagoon.view'),
          sizeSqm: 140,
          occupancy: t('demo.2.rooms.lagoon.occupancy'),
          gallery: JSON.stringify([
            'https://images.unsplash.com/photo-1582719508461-905c673771fd?w=800&q=80',
            'https://images.unsplash.com/photo-1590523741831-ab7e8b8f9c7f?w=800&q=80',
          ]),
          features: JSON.stringify(
            t('demo.2.rooms.lagoon.features', { returnObjects: true }) as string[]
          ),
        },
        {
          id: 202,
          name: 'Ocean Suite',
          nameZh: t('demo.2.rooms.ocean.nameZh'),
          description: 'Spacious suite with private pool.',
          descriptionZh: t('demo.2.rooms.ocean.description'),
          pricePerNight: 20800,
          maxGuests: 4,
          inventory: 2,
          imageUrl:
            'https://images.unsplash.com/photo-1590523741831-ab7e8b8f9c7f?w=600',
          amenities: JSON.stringify(
            t('demo.2.rooms.ocean.amenities', { returnObjects: true }) as string[]
          ),
          bedType: '2 King',
          view: t('demo.2.rooms.ocean.view'),
          sizeSqm: 280,
          occupancy: t('demo.2.rooms.ocean.occupancy'),
          gallery: JSON.stringify([
            'https://images.unsplash.com/photo-1590523741831-ab7e8b8f9c7f?w=800&q=80',
            'https://images.unsplash.com/photo-1544144433-d50aff500b91?w=800&q=80',
          ]),
          features: JSON.stringify(
            t('demo.2.rooms.ocean.features', { returnObjects: true }) as string[]
          ),
        },
      ],
      experiences: [],
      retreats: [],
    },
    3: {
      id: 3,
      name: 'Azure Bay',
      nameZh: t('demo.3.nameZh'),
      description: 'Beachfront villas with direct reef access.',
      descriptionZh: t('demo.3.description'),
      location: 'South Ari Atoll, Maldives',
      pricePerNight: 3200,
      maxGuests: 3,
      imageUrl:
        'https://images.unsplash.com/photo-1544550581-5f7ceaf7f992?w=1200&q=80',
      amenities: JSON.stringify(
        t('demo.3.amenities', { returnObjects: true }) as string[]
      ),
      gallery: JSON.stringify([
        'https://images.unsplash.com/photo-1544550581-5f7ceaf7f992?w=1200&q=80',
        'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=800&q=80',
        'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&q=80',
        'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=800&q=80',
        'https://images.unsplash.com/photo-1519046904884-53103b34b206?w=800&q=80',
        'https://images.unsplash.com/photo-1506929562872-bb421503ef21?w=800&q=80',
      ]),
      facilities: JSON.stringify(
        t('demo.3.facilities', { returnObjects: true }) as Facility[]
      ),
      locationDetails: JSON.stringify(
        t('demo.3.location', { returnObjects: true }) as LocationDetails
      ),
      story: JSON.stringify(
        t('demo.3.story', { returnObjects: true }) as Story
      ),
      roomTypes: [
        {
          id: 301,
          name: 'Lagoon Villa',
          nameZh: t('demo.3.rooms.lagoon.nameZh'),
          description: 'Overwater villa with lagoon views.',
          descriptionZh: t('demo.3.rooms.lagoon.description'),
          pricePerNight: 3200,
          maxGuests: 2,
          inventory: 3,
          imageUrl:
            'https://images.unsplash.com/photo-1582719508461-905c673771fd?w=600',
          amenities: JSON.stringify(
            t('demo.3.rooms.lagoon.amenities', { returnObjects: true }) as string[]
          ),
          bedType: 'King',
          view: t('demo.3.rooms.lagoon.view'),
          sizeSqm: 95,
          occupancy: t('demo.3.rooms.lagoon.occupancy'),
          gallery: JSON.stringify([
            'https://images.unsplash.com/photo-1582719508461-905c673771fd?w=800&q=80',
            'https://images.unsplash.com/photo-1590523741831-ab7e8b8f9c7f?w=800&q=80',
          ]),
          features: JSON.stringify(
            t('demo.3.rooms.lagoon.features', { returnObjects: true }) as string[]
          ),
        },
        {
          id: 302,
          name: 'Ocean Suite',
          nameZh: t('demo.3.rooms.ocean.nameZh'),
          description: 'Spacious suite with private pool.',
          descriptionZh: t('demo.3.rooms.ocean.description'),
          pricePerNight: 5200,
          maxGuests: 4,
          inventory: 2,
          imageUrl:
            'https://images.unsplash.com/photo-1590523741831-ab7e8b8f9c7f?w=600',
          amenities: JSON.stringify(
            t('demo.3.rooms.ocean.amenities', { returnObjects: true }) as string[]
          ),
          bedType: '2 King',
          view: t('demo.3.rooms.ocean.view'),
          sizeSqm: 180,
          occupancy: t('demo.3.rooms.ocean.occupancy'),
          gallery: JSON.stringify([
            'https://images.unsplash.com/photo-1590523741831-ab7e8b8f9c7f?w=800&q=80',
            'https://images.unsplash.com/photo-1544144433-d50aff500b91?w=800&q=80',
          ]),
          features: JSON.stringify(
            t('demo.3.rooms.ocean.features', { returnObjects: true }) as string[]
          ),
        },
      ],
      experiences: [],
      retreats: [],
    },
  };

  const demoProperty = demoProperties[Number(id)] || demoProperties[1];

  async function fetchProperty() {
    try {
      const res = await client.api.fetch(`/api/public/properties/${id}`);
      if (!res.ok) throw new Error(`API ${res.status}`);
      const data = await res.json();
      if (data.data) {
        setProperty(data.data);
      } else {
        setProperty(demoProperty);
      }
    } catch (err) {
      console.error('Property fetch failed, using demo data:', err);
      setProperty(demoProperty);
    } finally {
      setLoading(false);
    }
  }

  async function handleInquirySubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!property) return;

    const room = selectedRoom || property.roomTypes[0];
    if (!room) return;

    if (!inquiryCheckIn) {
      alert(t('form.selectCheckIn'));
      return;
    }
    const checkInDate = new Date(inquiryCheckIn);
    const checkOutDate = new Date(checkInDate);
    checkOutDate.setDate(checkOutDate.getDate() + inquiryDays);

    const totalAmount =
      (room.pricePerNight || property.pricePerNight) * inquiryDays +
      selectedActivities.reduce((sum, a) => sum + (a.price || 0), 0);

    try {
      const res = await client.api.fetch('/api/public/bookings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: inquiryName,
          email: inquiryEmail,
          phone: normalizeHKPhone(inquiryPhone),
          property_id: property.id,
          room_type_id: room.id,
          check_in: Math.floor(checkInDate.getTime() / 1000),
          check_out: Math.floor(checkOutDate.getTime() / 1000),
          guests: 1,
          total_amount: totalAmount,
          currency: 'HKD',
          addons: selectedActivities.map((a) => ({
            type: a.type,
            id: a.id,
            name: a.name,
            nameZh: a.name,
          })),
          referral_code: getRefCode(),
        }),
      });
      if (!res.ok) {
        console.error('Booking submit failed:', await res.text());
        return;
      }
      setInquirySubmitted(true);
    } catch (err) {
      console.error('Booking submit error:', err);
    }
  }

  if (loading)
    return (
      <div className="pt-24 flex justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#0a4c6b] border-t-transparent rounded-full" />
      </div>
    );
  if (!property)
    return (
      <div className="pt-24 text-center text-gray-500">{t('notFound')}</div>
    );

  const amenities = property.amenities ? JSON.parse(property.amenities) : [];
  const referencePrice = selectedRoom
    ? selectedRoom.pricePerNight
    : property.pricePerNight;
  const referenceTotal = referencePrice * inquiryDays +
    selectedActivities.reduce((sum, a) => sum + (a.price || 0), 0);

  const gallery = safeJsonParse<string[]>(property.gallery, []);
  const facilities = safeJsonParse<Facility[]>(property.facilities, []);
  const availableItems: ExperienceItem[] = [
    ...(property.experiences || []).map((e) => ({ ...e, type: 'experience' as const })),
    ...(property.retreats || []).map((r) => ({ ...r, type: 'retreat' as const })),
  ];
  const locationDetails = safeJsonParse<LocationDetails>(
    property.locationDetails,
    null
  );
  const story = safeJsonParse<Story>(property.story, null);

  return (
    <div className="pt-16">
      {/* Hero Image */}
      <div className="relative h-[50vh] overflow-hidden">
        <img
          src={property.imageUrl || ''}
          alt={localizedName(property)}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        <div className="absolute bottom-8 left-8 text-white">
          <p className="text-white/80 text-sm">{property.location}</p>
          <h1 className="text-4xl font-bold">{localizedName(property)}</h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Main content */}
          <div className="lg:col-span-2 space-y-12">
            {/* Story */}
            {story && (
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 sm:p-8">
                <h2 className="text-2xl font-bold text-[#0d1b2a] mb-4 font-serif">
                  {story.title}
                </h2>
                <p className="text-gray-600 leading-relaxed italic">
                  {story.content}
                </p>
              </div>
            )}

            {/* Gallery */}
            {gallery.length > 0 && (
              <div>
                <h2 className="text-2xl font-bold text-[#0d1b2a] mb-4 font-serif">
                  {t('sections.gallery')}
                </h2>
                <div className="space-y-4">
                  <div className="aspect-[16/9] rounded-2xl overflow-hidden shadow-lg bg-gray-100">
                    <img
                      src={gallery[activeImage]}
                      alt={`${localizedName(property)} ${t('sections.gallery')}`}
                      className="w-full h-full object-cover transition duration-500"
                    />
                  </div>
                  <div className="grid grid-cols-4 sm:grid-cols-6 gap-3">
                    {gallery.map((src, i) => (
                      <button
                        key={i}
                        onClick={() => setActiveImage(i)}
                        className={`aspect-square rounded-xl overflow-hidden border-2 transition ${
                          activeImage === i
                            ? 'border-[#0a4c6b]'
                            : 'border-transparent hover:border-[#2ec4b6]'
                        }`}
                      >
                        <img
                          src={src}
                          alt={`${localizedName(property)} ${t('gallery.thumbnail')} ${i + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Room Types */}
            <div>
              <h2 className="text-2xl font-bold text-[#0d1b2a] mb-4 font-serif">
                {t('sections.rooms')}
              </h2>

              {/* Room Cards Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {property.roomTypes.map((room) => {
                  const roomFeatures = safeJsonParse<string[]>(
                    room.features,
                    []
                  );
                  const isSelected = selectedRoom?.id === room.id;
                  return (
                    <button
                      key={room.id}
                      onClick={() => setSelectedRoom(room)}
                      className={`text-left bg-white rounded-2xl shadow-lg border overflow-hidden transition transform hover:-translate-y-1 ${
                        isSelected
                          ? 'border-[#0a4c6b] ring-2 ring-[#0a4c6b]/20'
                          : 'border-gray-100 hover:border-[#2ec4b6]'
                      }`}
                    >
                      <div className="aspect-[4/3] overflow-hidden bg-gray-100">
                        <img
                          src={
                            room.imageUrl || property.imageUrl || ''
                          }
                          alt={localizedName(room)}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="p-5 space-y-3">
                        <div>
                          <h3 className="text-xl font-bold text-[#0d1b2a]">
                            {localizedName(room)}
                          </h3>
                          <p className="text-sm text-gray-500">{localizedName(room)}</p>
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600">
                          {room.bedType && (
                            <span>{t('room.bedType', { value: room.bedType })}</span>
                          )}
                          {room.view && <span>{t('room.view', { value: room.view })}</span>}
                          {room.sizeSqm && (
                            <span>{t('room.size', { value: room.sizeSqm })}</span>
                          )}
                          {room.occupancy && (
                            <span>{t('room.occupancy', { value: room.occupancy })}</span>
                          )}
                        </div>
                        {roomFeatures.length > 0 && (
                          <div className="flex flex-wrap gap-1.5">
                            {roomFeatures.slice(0, 3).map((feature, i) => (
                              <span
                                key={i}
                                className="text-xs bg-[#f0f9f7] text-[#0a4c6b] px-2 py-0.5 rounded"
                              >
                                {feature}
                              </span>
                            ))}
                          </div>
                        )}
                        <p className="text-lg font-bold text-[#0a4c6b]">
                          HK${room.pricePerNight.toLocaleString()}
                          <span className="text-sm font-normal text-gray-500">
                            {t('room.perNight')}
                          </span>
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Expanded Room Detail */}
              <AnimatePresence mode="wait">
                {selectedRoom && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden mt-8"
                  >
                    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 sm:p-8">
                      {(() => {
                        const room = selectedRoom;
                        const roomAmenities = safeJsonParse<string[]>(
                          room.amenities,
                          []
                        );
                        const roomFeatures = safeJsonParse<string[]>(
                          room.features,
                          []
                        );
                        const roomGallery = safeJsonParse<string[]>(
                          room.gallery,
                          [room.imageUrl || property.imageUrl || '']
                        );
                        return (
                          <div className="space-y-6">
                            <div className="flex items-start justify-between gap-4">
                              <div>
                                <h3 className="text-2xl font-bold text-[#0d1b2a]">
                                  {localizedName(room)}
                                </h3>
                                <p className="text-gray-500">{localizedName(room)}</p>
                              </div>
                              <button
                                onClick={() => setSelectedRoom(null)}
                                className="text-sm text-gray-500 hover:text-[#0a4c6b] transition"
                              >
                                {t('room.collapse')}
                              </button>
                            </div>

                            {/* Room Gallery Carousel */}
                            {roomGallery.length > 0 && (
                              <div className="space-y-3">
                                <div className="aspect-[16/9] rounded-2xl overflow-hidden shadow-md bg-gray-100">
                                  <img
                                    src={roomGallery[roomGalleryIndex]}
                                    alt={`${localizedName(room)} ${t('sections.gallery')}`}
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                                {roomGallery.length > 1 && (
                                  <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                                    {roomGallery.map((src, i) => (
                                      <button
                                        key={i}
                                        onClick={() =>
                                          setRoomGalleryIndex(i)
                                        }
                                        className={`aspect-square rounded-xl overflow-hidden border-2 transition ${
                                          roomGalleryIndex === i
                                            ? 'border-[#0a4c6b]'
                                            : 'border-transparent hover:border-[#2ec4b6]'
                                        }`}
                                      >
                                        <img
                                          src={src}
                                          alt={`${localizedName(room)} ${t('gallery.thumbnail')} ${i + 1}`}
                                          className="w-full h-full object-cover"
                                        />
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}

                            {localizedDescription(room) && (
                              <p className="text-gray-600 leading-relaxed">
                                {localizedDescription(room)}
                              </p>
                            )}

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                              {roomAmenities.length > 0 && (
                                <div>
                                  <h4 className="font-bold text-[#0d1b2a] mb-2">
                                    {t('room.amenities')}
                                  </h4>
                                  <div className="flex flex-wrap gap-2">
                                    {roomAmenities.map((a, i) => (
                                      <span
                                        key={i}
                                        className="text-sm bg-[#f0f9f7] text-[#0a4c6b] px-3 py-1 rounded-full"
                                      >
                                        {a}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {roomFeatures.length > 0 && (
                                <div>
                                  <h4 className="font-bold text-[#0d1b2a] mb-2">
                                    {t('room.features')}
                                  </h4>
                                  <div className="flex flex-wrap gap-2">
                                    {roomFeatures.map((f, i) => (
                                      <span
                                        key={i}
                                        className="text-sm bg-[#f8f5ed] text-[#B8902F] px-3 py-1 rounded-full"
                                      >
                                        {f}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>

                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-t pt-6">
                              <div>
                                <p className="text-sm text-gray-500">
                                  {t('room.referencePrice')}
                                </p>
                                <p className="text-3xl font-bold text-[#0a4c6b]">
                                  HK${room.pricePerNight.toLocaleString()}
                                  <span className="text-base font-normal text-gray-500">
                                    {t('room.perNight')}
                                  </span>
                                </p>
                                <p className="text-sm text-gray-500">
                                  {t('room.remaining', { count: room.inventory })}
                                </p>
                              </div>
                              <button
                                onClick={scrollToInquiry}
                                className="bg-[#B8902F] hover:bg-[#9a7a28] text-white px-8 py-3 rounded-xl font-medium transition"
                              >
                                {t('room.select')}
                              </button>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>


            {/* Facilities */}
            {facilities.length > 0 && (
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 sm:p-8">
                <h2 className="text-2xl font-bold text-[#0d1b2a] mb-6 font-serif">
                  {t('sections.facilities')}
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {facilities.map((f, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-3 bg-[#f0f9f7] rounded-xl p-4"
                    >
                      <span className="text-2xl">{f.icon}</span>
                      <span className="text-sm font-medium text-[#0d1b2a]">
                        {f.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Activities */}
            {availableItems.length > 0 && (
              <div className="bg-[#f8fbfc] rounded-2xl border border-[#0a4c6b]/10 p-6 sm:p-8">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-[#0d1b2a] mb-2 font-serif">
                      {t('activities.title')}
                    </h2>
                    <p className="text-gray-600">
                      {t('activities.hint')}
                    </p>
                  </div>
                  <span className="inline-flex items-center justify-center px-4 py-2 bg-[#0a4c6b] text-white rounded-full text-sm font-medium">
                    {t('activities.selectedCount', { count: selectedActivities.length })}
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {availableItems.map((a) => {
                    const isSelected = selectedActivities.some(
                      (sa) => sa.id === a.id && sa.type === a.type
                    );
                    return (
                      <div
                        key={`${a.type}-${a.id}`}
                        role="button"
                        tabIndex={0}
                        aria-pressed={isSelected}
                        onClick={() =>
                          setSelectedActivities((prev) =>
                            isSelected
                              ? prev.filter((sa) => !(sa.id === a.id && sa.type === a.type))
                              : [...prev, a]
                          )
                        }
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setSelectedActivities((prev) =>
                              isSelected
                                ? prev.filter((sa) => !(sa.id === a.id && sa.type === a.type))
                                : [...prev, a]
                            );
                          }
                        }}
                        className={`text-left bg-white rounded-2xl shadow-lg border overflow-hidden transition cursor-pointer flex flex-col ${
                          isSelected
                            ? 'border-[#0a4c6b] ring-2 ring-[#0a4c6b]/20'
                            : 'border-gray-100 hover:border-[#2ec4b6]'
                        }`}
                      >
                        <div className="relative aspect-[16/10] overflow-hidden">
                          <img
                            src={a.imageUrl || '/images/placeholder.jpg'}
                            alt={localizedName(a)}
                            className="w-full h-full object-cover"
                          />
                          {isSelected && (
                            <div className="absolute top-3 right-3 bg-[#0a4c6b] text-white w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold shadow-md">
                              ✓
                            </div>
                          )}
                          {a.price != null && (
                            <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-xs font-bold bg-white/95 text-[#0a4c6b] shadow-sm">
                              HK${a.price.toLocaleString()}
                            </span>
                          )}
                          <span className="absolute bottom-3 left-3 px-2.5 py-1 rounded-full text-xs font-medium bg-white/90 text-[#0a4c6b]">
                            {a.type === 'retreat' ? t('activities.retreat') : t('activities.experience')}
                          </span>
                        </div>
                        <div className="p-5 flex flex-col flex-1">
                          <h3 className="font-bold text-[#0d1b2a] mb-2">
                            {localizedName(a)}
                          </h3>
                          <p className="text-sm text-gray-600 leading-relaxed line-clamp-3 mb-4 flex-1">
                            {localizedDescription(a)}
                          </p>
                          <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                            <span className={`text-xs font-medium ${isSelected ? 'text-[#0a4c6b]' : 'text-gray-400'}`}>
                              {isSelected ? t('activities.added') : t('activities.tapToAdd')}
                            </span>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedExperience(a);
                              }}
                              className="text-sm font-medium text-[#0a4c6b] hover:text-[#083d56] underline underline-offset-2"
                            >
                              {t('activities.details')}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Location & Nearby */}
            {locationDetails && (
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 sm:p-8">
                <h2 className="text-2xl font-bold text-[#0d1b2a] mb-4 font-serif">
                  {t('sections.location')}
                </h2>
                <p className="text-gray-600 leading-relaxed mb-6">
                  {locationDetails.description}
                </p>
                <div className="aspect-[16/9] rounded-2xl overflow-hidden shadow-md mb-6">
                  <img
                    src={locationDetails.mapImage}
                    alt={`${localizedName(property)} ${t('sections.locationMap')}`}
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="font-bold text-[#0d1b2a] mb-3">{t('sections.nearby')}</h3>
                <ul className="space-y-2">
                  {locationDetails.nearby.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-gray-600">
                      <span className="text-[#2ec4b6] mt-1">✓</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}


            {/* Inquiry CTA */}
            <div className="bg-gradient-to-br from-[#0a4c6b] to-[#0d1b2a] rounded-2xl p-8 sm:p-10 text-center text-white shadow-lg">
              <h2 className="text-2xl sm:text-3xl font-bold mb-3 font-serif">
                {t('cta.title')}
              </h2>
              <p className="text-white/80 mb-6 max-w-xl mx-auto">
                {t('cta.description')}
              </p>
              <button
                onClick={scrollToInquiry}
                className="inline-flex items-center justify-center bg-[#B8902F] hover:bg-[#9a7a28] text-white px-8 py-3 rounded-xl font-medium transition"
              >
                {t('cta.contactButler')}
              </button>
            </div>
          </div>

          {/* Booking Sidebar */}
          <div className="lg:col-span-1">
            <div
              id="inquiry-form"
              className="sticky top-20 bg-white border border-gray-200 rounded-2xl p-6 shadow-sm"
            >
              <h3 className="text-lg font-semibold text-[#0d1b2a] mb-1">{t('form.title')}</h3>
              <p className="text-sm text-gray-500 mb-4">{t('form.subtitle')}</p>

              {/* Social proof badge */}
              <div className="flex items-center gap-2 mb-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                <span className="text-lg">👥</span>
                <div>
                  <p className="text-xs font-medium text-blue-800">{t('form.socialProof.title')}</p>
                  <p className="text-xs text-blue-600">{t('form.socialProof.subtitle')}</p>
                </div>
              </div>
              {inquirySubmitted ? (
                <div className="bg-[#f0f9f7] border border-[#2ec4b6]/20 rounded-xl p-5 text-center">
                  <p className="text-[#0a4c6b] font-medium leading-relaxed">
                    {t('form.submitted')}
                  </p>
                </div>
              ) : (
                <form onSubmit={handleInquirySubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('form.name')}
                    </label>
                    <input
                      type="text"
                      required
                      value={inquiryName}
                      onChange={(e) => setInquiryName(e.target.value)}
                      className="w-full border rounded-lg px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('form.email')}
                    </label>
                    <input
                      type="email"
                      required
                      value={inquiryEmail}
                      onChange={(e) => setInquiryEmail(e.target.value)}
                      className="w-full border rounded-lg px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('form.phone')}
                    </label>
                    <input
                      type="tel"
                      required
                      value={inquiryPhone}
                      placeholder={t('form.phonePlaceholder')}
                      onChange={(e) => setInquiryPhone(e.target.value)}
                      className="w-full border rounded-lg px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('form.checkIn')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      required
                      value={inquiryCheckIn}
                      onChange={(e) => setInquiryCheckIn(e.target.value)}
                      className="w-full border rounded-lg px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('form.days')}
                    </label>
                    <select
                      required
                      value={inquiryDays}
                      onChange={(e) => setInquiryDays(Number(e.target.value))}
                      className="w-full border rounded-lg px-3 py-2 text-sm"
                    >
                      {Array.from({ length: 14 }, (_, i) => i + 1).map(
                        (n) => (
                          <option key={n} value={n}>
                            {t('form.dayOption', { n })}
                          </option>
                        )
                      )}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('form.message')}
                    </label>
                    <textarea
                      value={inquiryMessage}
                      onChange={(e) => setInquiryMessage(e.target.value)}
                      rows={3}
                      className="w-full border rounded-lg px-3 py-2 text-sm"
                      placeholder={t('form.messagePlaceholder')}
                    />
                  </div>

                  {selectedActivities.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('form.selectedAddons')}
                      </label>
                      <div className="space-y-2">
                        {selectedActivities.map((a) => (
                          <div
                            key={`${a.type}-${a.id}`}
                            className="flex items-center justify-between bg-[#f0f9f7] rounded-lg px-3 py-2 text-sm"
                          >
                            <span className="text-[#0a4c6b] font-medium">
                              {localizedName(a)}
                              <span className="ml-2 text-xs text-gray-500">
                                ({a.type === 'retreat' ? t('activities.retreat') : t('activities.experience')})
                              </span>
                            </span>
                            <button
                              type="button"
                              onClick={() =>
                                setSelectedActivities((prev) =>
                                  prev.filter((sa) => !(sa.id === a.id && sa.type === a.type))
                                )
                              }
                              className="text-gray-400 hover:text-red-500 transition"
                              aria-label={t('form.removeAddon')}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="border-t pt-4">
                    <p className="text-sm text-gray-500 mb-1">{t('form.totalLabel')}</p>
                    <p className="text-2xl font-bold text-[#0a4c6b]">
                      HK${referenceTotal.toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {t('form.totalNote', { nights: inquiryDays, count: selectedActivities.length })}
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={!inquiryCheckIn}
                    className={`w-full py-3 rounded-xl font-medium transition ${
                      inquiryCheckIn
                        ? 'bg-[#0a4c6b] text-white hover:bg-[#083d56]'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    {t('form.submit')}
                  </button>
                  <p className="text-xs text-gray-500 text-center">
                    {t('form.noPressure')}
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add-on Experience Detail Modal */}
      <AnimatePresence>
        {selectedExperience && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
            onClick={() => setSelectedExperience(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="relative aspect-video">
                <img
                  src={selectedExperience.imageUrl || '/images/placeholder.jpg'}
                  alt={localizedName(selectedExperience)}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                <button
                  type="button"
                  onClick={() => setSelectedExperience(null)}
                  className="absolute top-4 right-4 bg-white/90 hover:bg-white text-gray-700 rounded-full p-2 transition"
                >
                  <X className="w-5 h-5" />
                </button>
                <div className="absolute bottom-4 left-4 text-white">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className="inline-block bg-[#B8902F] text-xs font-medium px-3 py-1 rounded-full">
                      {selectedExperience.type === 'retreat' ? t('activities.retreat') : t('activities.experience')}
                    </span>
                    {selectedActivities.some(
                      (sa) => sa.id === selectedExperience.id && sa.type === selectedExperience.type
                    ) && (
                      <span className="inline-flex items-center gap-1 bg-green-500 text-white text-xs font-medium px-3 py-1 rounded-full">
                        <Check className="w-3 h-3" />
                        {t('modal.added')}
                      </span>
                    )}
                  </div>
                  <h3 className="text-2xl font-bold">{localizedName(selectedExperience)}</h3>
                </div>
              </div>
              <div className="p-6 sm:p-8 space-y-6">
                <p className="text-gray-600 leading-relaxed">
                  {localizedDescription(selectedExperience)}
                </p>

                <div className="grid grid-cols-2 gap-4">
                  {selectedExperience.duration && (
                    <div className="flex items-center gap-3 bg-[#f8fafb] rounded-xl p-4">
                      <Clock className="w-5 h-5 text-[#2ec4b6]" />
                      <div>
                        <p className="text-xs text-gray-500">{t('modal.duration')}</p>
                        <p className="font-medium text-[#0d1b2a]">{selectedExperience.duration}</p>
                      </div>
                    </div>
                  )}
                  {selectedExperience.groupSize && (
                    <div className="flex items-center gap-3 bg-[#f8fafb] rounded-xl p-4">
                      <Users className="w-5 h-5 text-[#2ec4b6]" />
                      <div>
                        <p className="text-xs text-gray-500">{t('modal.groupSize')}</p>
                        <p className="font-medium text-[#0d1b2a]">{selectedExperience.groupSize}</p>
                      </div>
                    </div>
                  )}
                </div>

                {(() => {
                  const includes = safeJsonParse<string[]>(selectedExperience.includes, []);
                  return includes.length > 0 ? (
                    <div>
                      <h4 className="font-semibold text-[#0d1b2a] mb-3">{t('modal.includes')}</h4>
                      <div className="flex flex-wrap gap-2">
                        {includes.map((item, idx) => (
                          <span
                            key={idx}
                            className="text-sm bg-[#f0f9f7] text-[#0a4c6b] px-3 py-1.5 rounded-full flex items-center gap-1"
                          >
                            <Check className="w-3.5 h-3.5" />
                            {item}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : null;
                })()}

                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  {selectedExperience.price != null ? (
                    <p className="text-lg font-bold text-[#0a4c6b]">HK${selectedExperience.price.toLocaleString()}</p>
                  ) : (
                    <p className="text-sm text-gray-500">{selectedExperience.priceNote}</p>
                  )}
                  {(() => {
                    const isSelected = selectedActivities.some(
                      (sa) => sa.id === selectedExperience.id && sa.type === selectedExperience.type
                    );
                    return (
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedActivities((prev) =>
                            isSelected
                              ? prev.filter((sa) => !(sa.id === selectedExperience.id && sa.type === selectedExperience.type))
                              : [...prev, selectedExperience]
                          );
                        }}
                        className={`px-6 py-2.5 rounded-xl text-sm font-semibold transition ${
                          isSelected
                            ? 'bg-green-50 text-green-700 border border-green-200 hover:bg-green-100'
                            : 'bg-[#0a4c6b] text-white hover:bg-[#083d56]'
                        }`}
                      >
                        {isSelected ? (
                          <span className="inline-flex items-center gap-1">
                            <Check className="w-4 h-4" />
                            {t('modal.added')}
                          </span>
                        ) : (
                          t('modal.add')
                        )}
                      </button>
                    );
                  })()}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
